import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useReactHookForm } from '../hooks/useReactHookForm';
import { ReactHookForm } from './hook-form/react-hook-form.component';
import { LoadingIndicator } from './loading-indicator.component';
import { useSnackbar } from '../hooks/use-snackbar.hook';
import { UserPermissionName } from '../models/user-permission-name';
import { useAuthorize } from '../hooks/use-authorize';

const styling = {
  dropZone: {
    padding: '27px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
};

export interface FileUploadRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  tempEntityID?: any;
}

interface GenericFileUploadDialogProps {
  title?: string;
  defaultData: any;
  fileRequestArgs: FileUploadRequestArgs;
  onSubmit: (formData: File | null) => void;
  onClose: () => void;
  isLoading?: boolean;
  acceptedFileTypes?: Record<string, string[]>;
  placeholder?: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 5 MB
// const MAX_FILE_SIZE = 5 * 1024;

export const GenericFileUploadDialog = ({
  title = 'Upload File',
  defaultData,
  fileRequestArgs,
  onSubmit,
  onClose,
  isLoading = false,
  acceptedFileTypes,
  placeholder,
}: GenericFileUploadDialogProps) => {
  const form = useReactHookForm({ defaultValues: defaultData });
  const [file, setFile] = useState<File | undefined>();

  const { showSnackbar } = useSnackbar();

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
  }, []);

  const dropzoneConfig = {
    maxSize: MAX_FILE_SIZE,
    onDrop: handleDrop,
    multiple: false,
    ...(acceptedFileTypes && { accept: acceptedFileTypes }),
    onDropRejected: (rejectedFiles: FileRejection[]) => {
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            showSnackbar(`${file.name} exceeds the 5MB file size limit`, 'error');
          } else {
            showSnackbar(`${file.name}: ${error.message}`, 'error');
          }
        });
      });
    },
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneConfig);

  const handleFormSubmit = async () => {
    const currentFile = file ?? null;
    await onSubmit(currentFile);
    setFile(undefined);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog open fullWidth maxWidth="md" aria-labelledby="upload-dialog-title">
      <DialogTitle id="upload-dialog-title" style={styling.dialogTitleStyle}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 10, top: 10, color: 'grey.500' }}
        >
          <Tooltip title={<Typography variant="body1">Close</Typography>}>
            <CloseIcon />
          </Tooltip>
        </IconButton>
      </DialogTitle>

      <ReactHookForm formName="FILE_UPLOAD_FORM" onSubmit={handleFormSubmit} formContextProps={form}>
        <DialogContent dividers style={styling.contentPadding}>
          <LoadingIndicator isLoading={isLoading} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div style={styling.dropZone} {...getRootProps()}>
                <input {...getInputProps()} />
                <Typography variant="body2" textAlign="center">
                  {isDragActive
                    ? 'Drop the file here...'
                    : file
                      ? `${file.name} selected`
                      : defaultData?.fileName
                        ? `${defaultData.fileName} is currently uploaded`
                        : !placeholder
                          ? 'Drag and drop a .xls, .xlsx, or .csv file here, or click to select one'
                          : placeholder}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12}>
              <div className="spaceBetween">
                <Button variant="outlined" onClick={onClose} style={{ width: '130px' }}>
                  Cancel
                </Button>
                <Button
                  disabled={isLoading || isReadOnlyPermission}
                  variant="contained"
                  type="button"
                  onClick={() => handleFormSubmit()}
                  style={{ width: '130px' }}
                >
                  Save
                </Button>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
      </ReactHookForm>
    </Dialog>
  );
};
